import {
  Cartesian3,
  Cartesian4,
  Color,
  DeveloperError,
  JulianDate,
  PostProcessStage,
} from "@cesium/engine";
import DitapViewer from "../basic/DitapViewer";

/**
 * 안개를 생성하고 표시하는 클래스.</br>
 * Class for creating and displaying fog.
 *
 * @alias DitapFog
 * @constructor
 * @param {object} fogOptions 다음과 같은 속성을 가진 객체.</br>Object with the following properties:
 * @param {DitapViewer} fogOptions.viewer 사용할 Ditap Viewer 인스턴스.</br>The DitapViewer instance to use.
 * @param {Number} fogOptions.longitude 안개의 중심 경도.</br>Longitude of the fog center.
 * @param {Number} fogOptions.latitude 안개의 중심 위도.</br>Latitude of the fog center.
 * @param {Number} fogOptions.radius 안개가 영향을 미치는 범위의 반지름(m).(기본값:100000)</br>Radius of the area affected by fog (in meters).(default: 100000)
 * @param {Number} fogOptions.range 안개의 가시거리(m).(기본값:500)</br>visibility of the fog (in meters).(default: 500)
 * @param {Number} fogOptions.color 안개의 색상.(기본값: new Color(0.9, 0.9, 0.9, 0.8))</br>Color of the fog.(default: new Color(0.9, 0.9, 0.9, 0.8))
 * @param {Boolean} fogOptions.isTimeBasedBrightness 시간에 따라 안개의 밝기 변화 여부.(기본값: false)</br>Determines whether the brightness of fog changes over time. (Default: false)
 *
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Ditap Fog' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *     fullscreenButton: true,
 * });
 *
 * // 안개 효과 색상
 * const fogColor = new Ditap.Color(0.9, 0.9, 0.9, 0.8);
 *
 * // 안개 효과 인스턴스 생성
 *  const fog = new Ditap.DitapFog({
 *      viewer: viewer,
 *      longitude: 126.978275264,
 *      latitude: 37.566642192,
 *      radius: 100000,
 *      range: 1000,
 *      color: fogColor,
 *      isTimeBasedBrightness: false
 *  });
 *
 * // 카메라 이동
 * viewer.camera.setView({
 *   destination: Ditap.Cartesian3.fromDegrees(126.978275264, 37.566642192, 200),
 *   orientation: {
 *     heading: Ditap.Math.toRadians(0),
 *     pitch: Ditap.Math.toRadians(-10),
 *     roll: 0,
 *   },
 * });
 *
 *  // 안개 효과 시작
 *  fog.start();
 *
 */
class DitapFog {
  constructor(fogOptions) {
    this._defaultColor = new Color(0.9, 0.9, 0.9, 0.8); // 디폴트 색상
    this._isStart = false;
    if (fogOptions.viewer instanceof DitapViewer) {
      /**
       * 사용할 Ditap Viewer 인스턴스.</br>
       * The DitapViewer instance to use.
       * @type {DitapViewer}
       *
       * */
      this.viewer = fogOptions.viewer;
    }

    this.originAtmosphereOption = {
      skyAtmosphere: {
        hueShift: this.viewer.scene.skyAtmosphere.hueShift,
        saturationShift: this.viewer.scene.skyAtmosphere.saturationShift,
        brightnessShift: this.viewer.scene.skyAtmosphere.brightnessShift,
      },
      fog: {
        density: this.viewer.scene.fog.density,
        minimumBrightness: this.viewer.scene.fog.minimumBrightness,
      },
    };
    const availAbleOptions = this.checkFogOptions(fogOptions);

    /**
     * 안개의 중심 경도.</br>
     * Longitude of the fog center.
     * @type {Number}
     */
    this.longitude = availAbleOptions.longitude;
    /**
     * 안개의 중심 위도.</br>
     * Latitude of the fog center.
     * @type {Number}
     */
    this.latitude = availAbleOptions.latitude;
    /**
     * 안개가 영향을 미치는 범위의 반지름(m).(기본값:100000)</br>
     * Radius of the area affected by fog (in meters).(default: 100000)
     * @type {Number}
     */
    this.radius = availAbleOptions.radius;
    this.range = availAbleOptions.range;
    this.color = availAbleOptions.color;
    this._maximumAlpha = availAbleOptions.color.alpha; // 알파 최대값: Color(0.7, 0.7, 0.7).withAlpha(0.8)
    /**
     * 시간에 따라 안개의 밝기 변화 여부.(기본값: false)</br>
     * Determines whether the brightness of fog changes over time. (Default: false)
     * @type {Boolean}
     */
    this.isTimeBasedBrightness = availAbleOptions.isTimeBasedBrightness;
  }

  /**
   * 안개의 가시거리(m).(기본값:500)</br>
   * visibility of the fog (in meters).(default: 500)
   * @type {Number}
   */
  get range() {
    return this._range;
  }

  set range(value) {
    this._range = value;
    if (this._isStart) {
      this.start();
    }
  }

  /**
   * 안개의 색상.(기본값: new Color(0.9, 0.9, 0.9, 0.8))</br>
   * Color of the fog.(default: new Color(0.9, 0.9, 0.9, 0.8))
   * @type {Number}
   */
  get color() {
    return this._color;
  }

  set color(value) {
    this._color = value;
    if (this._isStart) {
      this.start();
    }
  }

  /**
   * 안개 포스트 프로세스 스테이지 생성.</br>
   * Creates a post-process stage for fog effect.
   *
   * @returns {PostProcessStage} 생성된 포스트 프로세스 스테이지.</br>Created post-process stage.
   */
  createPostProcessStage() {
    const fragmentShaderSource = `
            float getDistance(sampler2D depthTexture, vec2 texCoords) 
            { 
                float depth = czm_unpackDepth(texture(depthTexture, texCoords)); 
                if (depth == 0.0) { 
                    return czm_infinity; 
                } 
                vec4 eyeCoordinate = czm_windowToEyeCoordinates(gl_FragCoord.xy, depth); 
                return -eyeCoordinate.z / eyeCoordinate.w; 
            } 
            float interpolateByDistance(vec4 nearFarScalar, float distance) 
            { 
                float startDistance = nearFarScalar.x; 
                float startValue = nearFarScalar.y; 
                float endDistance = nearFarScalar.z; 
                float endValue = nearFarScalar.w; 
                float t = clamp((distance - startDistance) / (endDistance - startDistance), 0.0, 1.0); 
                return mix(startValue, endValue, t); 
            } 
            vec4 alphaBlend(vec4 sourceColor, vec4 destinationColor) 
            { 
                return sourceColor * vec4(sourceColor.aaa, 1.0) + destinationColor * (1.0 - sourceColor.a); 
            } 
            uniform sampler2D colorTexture; 
            uniform sampler2D depthTexture; 
            uniform vec4 fogByDistance; 
            uniform vec4 fogColor; 
            in vec2 v_textureCoordinates; 
            void main(void) 
            { 
                float distance = getDistance(depthTexture, v_textureCoordinates); 
                vec4 sceneColor = texture(colorTexture, v_textureCoordinates); 
                float blendAmount = interpolateByDistance(fogByDistance, distance); 
                vec4 finalFogColor = vec4(fogColor.rgb, fogColor.a * blendAmount); 
                out_FragColor = alphaBlend(finalFogColor, sceneColor); 
            }`;

    const postProcessStage = new PostProcessStage({
      fragmentShader: fragmentShaderSource,
      uniforms: {
        fogByDistance: new Cartesian4(10, 0.0, this.range, 1.0),
        fogColor: Color.WHITE,
        // fogColor: Color.BLACK.withAlpha(0.8),
      },
    });

    return postProcessStage;
  }

  /**
   * 시간과 안개 영역에 따른 안개의 색상을 조회.</br>
   * Retrieves the color of the fog.
   *
   * @returns {Color} fogColor 안개 색상</br> Color of the fog.
   */
  getFogColor() {
    // 안개 색상 조회
    const viewer = this.viewer;
    const longitude = this.longitude;
    const latitude = this.latitude;
    const radius = this.radius;

    const centerCartesian = Cartesian3.fromDegrees(longitude, latitude);

    let color;
    if (this.isTimeBasedBrightness) {
      color = this.getColorByTime();
    } else {
      color = this.color;
    }

    const distanceFromCenter = Cartesian3.distance(
      centerCartesian,
      viewer.camera.position
    );
    if (distanceFromCenter > radius) {
      // 카메라가 안개 지역 바깥에 있는 경우
      // 설정한 안개지역의 바깥에서 반지름만큼 안개 서서히 얕아짐
      let alpha = 1 - (distanceFromCenter - radius) / radius;
      if (alpha < 0) {
        alpha = 0;
      }
      if (alpha > this._maximumAlpha) {
        alpha = this._maximumAlpha;
      }
      return color.withAlpha(alpha);
    }
    return color.withAlpha(this._maximumAlpha);
  }

  /**
   * 시간과 따른 안개의 색상을 조회.</br>
   * Retrieves the color of the fog.
   *
   * @returns {Color} fogColor 안개 색상</br> Color of the fog.
   */
  getColorByTime() {
    // 시간에 따른 색상 조회
    // 가장 밝은 시간 12:00, 가장 어두운 시간 00:30
    const currentTime = this.viewer.clock.currentTime;
    const currentJulianDate = JulianDate.toDate(currentTime); // JulianDate를 JavaScript Date 객체로 변환

    const hours = currentJulianDate.getHours();
    const minutes = currentJulianDate.getMinutes();

    // 시간을 0-24 범위로 변환하여 표현합니다.
    let timeOfDay = hours + minutes / 60;
    // 시간을 0-1 사이의 값으로 정규화합니다.
    timeOfDay = timeOfDay / 24;

    // 흰색에서 검은색으로 점차적으로 변하는 색상 계산
    let brightness;
    if (timeOfDay <= 0.5) {
      // 00:30 이후부터 12:00까지, 검은색에서 흰색으로 점차 밝아지는 과정
      brightness = timeOfDay * 2;
    } else {
      // 12:00 이후부터 00:30까지, 흰색에서 검은색으로 점차 어두워지는 과정
      brightness = (1 - timeOfDay) * 2;
    }

    return this.getBrightnessColor(brightness, this.color);
  }

  /*
   * 밝기 조절 함수 0 ~ 1
   */
  getBrightnessColor(brightness, color) {
    const red = color.red * brightness;
    const green = color.green * brightness;
    const blue = color.blue * brightness;
    const alpha = color.alpha * brightness;

    return new Color(red, green, blue, alpha);
  }

  /**
   * 카메라 이벤트 생성.</br>
   * Creates a camera event to apply fog effect.
   */
  createCameraEvent() {
    const viewer = this.viewer;
    this.cameraMoveEvent = viewer.clock.onTick.addEventListener(() => {
      this.postProcessStage.uniforms.fogColor = this.getFogColor();
    });
  }

  /**
   * 카메라 이벤트 제거.</br>
   * Removes the camera event.
   */
  removeCameraEvent() {
    if (this.cameraMoveEvent) {
      this.viewer.clock.onTick.removeEventListener(this.cameraMoveEvent);
    }
  }

  /**
   * 안개 효과 시작.</br>
   * Starts the fog effect.
   */
  start() {
    this.stop();

    this.postProcessStage = this.createPostProcessStage();
    this.viewer.scene.postProcessStages.add(this.postProcessStage);
    this.createCameraEvent();

    this.setAtmosphereCloudy();
    this._isStart = true;
  }

  /**
   * 안개 효과를 중지.</br>
   * Stops the fog effect.
   */
  stop() {
    this.removeCameraEvent();
    this.setOriginAtmosphere();
    if (
      this.viewer.scene.postProcessStages.contains(this.postProcessStage) &&
      this.postProcessStage
    ) {
      this.viewer.scene.postProcessStages.remove(this.postProcessStage);
    }
    this._isStart = false;
  }

  /**
   * 객체를 파괴하고 관련 리소스를 해제합니다. </br>
   * Destroys the object and releases associated resources.
   */
  destroy() {
    // 구름 효과 중지
    this.stop();

    // 뷰어 연결 해제
    this.viewer = undefined;
  }

  /*
   * 하늘을 흐린 상태로 설정.<br>
   * Set the sky to cloudy state based on the precipitation type.
   */
  setAtmosphereCloudy() {
    this.viewer.scene.skyAtmosphere.hueShift = -0.97;
    this.viewer.scene.skyAtmosphere.saturationShift = -0.4;
    this.viewer.scene.skyAtmosphere.brightnessShift = -0.4;
    this.viewer.scene.fog.density = 0.00025;
    this.viewer.scene.fog.minimumBrightness = 0.01;
  }

  setOriginAtmosphere() {
    this.viewer.scene.skyAtmosphere.hueShift = this.originAtmosphereOption.skyAtmosphere.hueShift;
    this.viewer.scene.skyAtmosphere.saturationShift = this.originAtmosphereOption.skyAtmosphere.saturationShift;
    this.viewer.scene.skyAtmosphere.brightnessShift = this.originAtmosphereOption.skyAtmosphere.brightnessShift;
    this.viewer.scene.fog.density = this.originAtmosphereOption.fog.density;
    this.viewer.scene.fog.minimumBrightness = this.originAtmosphereOption.fog.minimumBrightness;
  }

  /**
   * 사용자가 설정한 안개 옵션을 유효성 검사 및 기본값 설정.</br>
   * Validates user-defined fog options and sets default values.
   *
   * @param {object} fogOptions 다음과 같은 속성을 가진 객체.</br>Object with the following properties:
   * @param {Number} fogOptions.longitude 안개의 중심 경도.</br>Longitude of the fog center.
   * @param {Number} fogOptions.latitude 안개의 중심 위도.</br>Latitude of the fog center.
   * @param {Number} fogOptions.radius 안개가 영향을 미치는 범위의 반지름(m).(기본값:100000)</br>Radius of the area affected by fog (in meters).(default: 100000)
   * @param {Number} fogOptions.range 안개의 가시거리(m).(기본값:500)</br>visibility of the fog (in meters).(default: 500)
   * @param {Number} fogOptions.color 안개의 색상.(기본값: new Color(0.9, 0.9, 0.9, 0.8))</br>Color of the fog.(default: new Color(0.9, 0.9, 0.9, 0.8))
   * @param {Boolean} fogOptions.isTimeBasedBrightness 시간에 따라 안개의 밝기 변화 여부.(기본값: false)</br>Determines whether the brightness of fog changes over time. (Default: false)
   * @returns {object} fogOptions 유효성 검사가 완료된 안개 옵션.</br> fog options after validation.
   */
  checkFogOptions(fog) {
    let radius = fog.radius;
    let range = fog.range;
    let isTimeBasedBrightness = fog.isTimeBasedBrightness;
    let color = fog.color;
    const longitude = fog.longitude;
    const latitude = fog.latitude;

    if (typeof longitude !== "number") {
      throw new DeveloperError("longitude invalid");
    }
    if (typeof latitude !== "number") {
      throw new DeveloperError("latitude invalid");
    }
    if (typeof radius !== "number") {
      radius = 100000;
    }
    if (radius < 0) {
      radius = 0;
    }
    if (typeof range !== "number") {
      range = 500;
    }
    if (range < 0) {
      range = 0;
    }
    if (typeof isTimeBasedBrightness === "undefined") {
      isTimeBasedBrightness = false;
    }
    if (!(color instanceof Color)) {
      color = this._defaultColor;
    }

    fog.radius = radius;
    fog.range = range;
    fog.color = color;
    fog.isTimeBasedBrightness = isTimeBasedBrightness;

    return fog;
  }
}
export default DitapFog;
